import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const FlagIcon: React.FC<{ code: string }> = ({ code }) => {
  const flags: Record<string, JSX.Element> = {
    en: (
      <svg width="24" height="16" viewBox="0 0 60 40" className="inline-block rounded shadow-sm border border-black/10">
        <rect width="60" height="40" fill="#012169"/>
        <path d="M0 0 L60 40 M60 0 L0 40" stroke="#FFF" strokeWidth="8"/>
        <path d="M0 0 L60 40 M60 0 L0 40" stroke="#C8102E" strokeWidth="5"/>
        <path d="M30 0 v40 M0 20 h60" stroke="#FFF" strokeWidth="13"/>
        <path d="M30 0 v40 M0 20 h60" stroke="#C8102E" strokeWidth="8"/>
      </svg>
    ),
    es: (
      <svg width="24" height="16" viewBox="0 0 60 40" className="inline-block rounded shadow-sm border border-black/10">
        <rect width="60" height="40" fill="#AA151B"/>
        <rect y="10" width="60" height="20" fill="#F1BF00"/>
      </svg>
    ),
    fr: (
      <svg width="24" height="16" viewBox="0 0 60 40" className="inline-block rounded shadow-sm border border-black/10">
        <rect width="60" height="40" fill="#FFF"/>
        <rect width="20" height="40" fill="#002395"/>
        <rect x="40" width="20" height="40" fill="#ED2939"/>
      </svg>
    ),
    de: (
      <svg width="24" height="16" viewBox="0 0 60 40" className="inline-block rounded shadow-sm border border-black/10">
        <rect width="60" height="40" fill="#000"/>
        <rect y="13.33" width="60" height="13.33" fill="#DD0000"/>
        <rect y="26.66" width="60" height="13.34" fill="#FFCE00"/>
      </svg>
    ),
    it: (
      <svg width="24" height="16" viewBox="0 0 60 40" className="inline-block rounded shadow-sm border border-black/10">
        <rect width="60" height="40" fill="#FFF"/>
        <rect width="20" height="40" fill="#009246"/>
        <rect x="40" width="20" height="40" fill="#CE2B37"/>
      </svg>
    )
  };

  return flags[code] || flags['en'];
};

const LanguageSwitcher: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'en', name: t('languages.english'), shortName: 'EN' },
    { code: 'es', name: t('languages.spanish'), shortName: 'ES' },
    { code: 'fr', name: t('languages.french'), shortName: 'FR' },
    { code: 'de', name: t('languages.german'), shortName: 'DE' },
    { code: 'it', name: t('languages.italian'), shortName: 'IT' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300"
        style={{
          background: '#ffffff',
          color: '#1e293b',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.02)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#ffffff';
        }}
      >
        <FlagIcon code={currentLanguage.code} />
        <span className="text-sm font-medium">{currentLanguage.shortName}</span>
        <ChevronDown size={14} style={{ color: '#94a3b8' }} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 rounded-xl shadow-xl z-50 min-w-48 overflow-hidden" style={{ background: '#ffffff', border: '1px solid rgba(0, 0, 0, 0.1)' }}>
          <div className="py-2">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className="w-full text-left px-4 py-3 transition-colors flex items-center justify-between"
                style={{
                  background: i18n.language === language.code ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                  color: i18n.language === language.code ? '#3b82f6' : '#1e293b'
                }}
                onMouseEnter={(e) => {
                  if (i18n.language !== language.code) {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.02)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (i18n.language !== language.code) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <div className="flex items-center gap-3">
                  <FlagIcon code={language.code} />
                  <span className="font-medium">{language.name}</span>
                </div>
                {i18n.language === language.code && (
                  <Check size={16} style={{ color: '#3b82f6' }} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;